import { Injectable, signal, inject, effect } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { User } from '@models';
import { LocalStorageKey, LocalStorageService } from '@services';
import { environment } from '@environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private localStorageService = inject(LocalStorageService);
  private http = inject(HttpClient);

  user = signal<User | null>(this.localStorageService.get<User>(LocalStorageKey.User));

  constructor() {
    effect(() => {
      const user = this.user();
      const cachedUser = this.localStorageService.get<User>(LocalStorageKey.User);
      if (cachedUser?.token === user?.token) {
        return;
      }

      if (user) {
        this.localStorageService.set(LocalStorageKey.User, user);
      } else {
        this.localStorageService.remove(LocalStorageKey.User);
      }

      window.location.reload();
    });
  }

  login(username: string, password: string): Observable<User> {
    return this.http
      .get<User>(`${environment.komainuHost}/authenticate/`, {
        headers: new HttpHeaders({
          Authorization: `Basic ${btoa(`${username}:${password}`)}`,
        }),
      })
      .pipe(
        tap((user: User) => {
          this.user.set(user);
        }),
      );
  }

  getOneTimeToken(): Observable<string> {
    const bearerToken = this.user()?.token;
    return this.http
      .get(`${environment.komainuHost}/ott/`, {
        responseType: 'text',
        headers: new HttpHeaders({
          Authorization: `Bearer ${bearerToken}`,
        }),
      });
  }

  logout() {
    this.user.set(null);
  }

  confirmPasswordRecovery(token: string, password: string): Observable<null> {
    return this.http.post<null>(
      `${environment.komainuHost}/recover/confirm/`,
      {
        password: password,
        recoveryToken: token,
      },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      },
    );
  }

  public readonly endpoints = {
    createGeologicalSample: new RegExp("^POST\\s\\S+\\s/dataset/" + environment.geologicalSampleDatasetId + "/record/$"),
    editGeologicalSample: new RegExp("^PUT\\s\\S+\\s/dataset/" + environment.geologicalSampleDatasetId + "/record/\\*$"),
    deleteGeologicalSample: new RegExp("^DELETE\\s\\S+\\s/dataset/" + environment.geologicalSampleDatasetId + "/record/\\*$"),
    createMapArchiveEntry: new RegExp("^POST\\s\\S+\\s/dataset/" + environment.mapArchiveDatasetId + "/record/$"),
    editMapArchiveEntry: new RegExp("^PUT\\s\\S+\\s/dataset/" + environment.mapArchiveDatasetId + "/record/\\*$"),
    deleteMapArchiveEntry: new RegExp("^DELETE\\s\\S+\\s/dataset/" + environment.mapArchiveDatasetId + "/record/\\*$"),
    createVesselEntry: new RegExp("^POST\\s\\S+\\s/dataset/" + environment.vesselDatasetId + "/record/$"),
    editVesselEntry: new RegExp("^PUT\\s\\S+\\s/dataset/" + environment.vesselDatasetId + "/record/\\*$"),
    deleteVesselEntry: new RegExp("^DELETE\\s\\S+\\s/dataset/" + environment.vesselDatasetId + "/record/\\*$"),
    createPlacename: /^POST\s\S+\s\/placename\/$/,
    editPlacename: /^PUT\s\S+\s\/placename\/\*$/,
    deletePlacename: /^DELETE\s\S+\s\/placename\/\*$/,
    createCase: /^POST\s\S+\s\/case\/$/,
    editCase: /^PUT\s\S+\s\/case\/\*$/,
    deleteCase: /^DELETE\s\S+\s\/case\/\*$/,
  };

  canInvoke(endpoint: RegExp): boolean {
    const user = this.user();

    if (!user) {
      return false;
    }

    if (user.accessLevel === 'admin') {
      return true;
    }

    return user.roles.some(r => r.endpoints.some(e => endpoint.test(e)));
  }
}
